// Incident source for the Incidents tab: what came in, when we answered and fixed it, and whether that met the agreement.

import {
  IncidentEntity,
  IncidentInput,
  IncidentPatch,
  IncidentPriorityValue,
  IncidentWorkEntity,
} from '@data/utils/types';
import { IncidentPause, deadlineAfterPauses, pauseSpans, workingMsBetween } from './incidentPauses';
import { SprintSpan, reportedInSprint } from './incidentSprints';

export type Priority = 'P1' | 'P2' | 'P3' | 'P4';

export const PRIORITIES: Priority[] = ['P1', 'P2', 'P3', 'P4'];

/** How the incident reached us. Route matters: a P1 is meant to be phoned in, not mailed. */
export type IncidentChannel = 'email' | 'slack' | 'phone' | 'phill';

export const CHANNELS: IncidentChannel[] = ['email', 'slack', 'phone', 'phill'];

export type SupportLevel = 'standard' | 'bronze' | 'silver' | 'gold';

/** Weakest promise first, which is the order the agreement itself is written in. */
export const LEVELS: SupportLevel[] = ['standard', 'bronze', 'silver', 'gold'];

export const priorityLabel = (priority: Priority, name: string): string => `${priority} - ${name}`;

export interface SlaTarget {
  priority: Priority;
  /** Hours to first respond. */
  responseHours: number;
  /** Hours to resolve, or to have a workaround in place. */
  resolutionHours: number;
  /** What the hourly rate is multiplied by for work at this priority. */
  rateMultiplier: number;
}

export interface SupportAgreement {
  level: SupportLevel;
  /** The service window in words, e.g. "Mon–Fri, 09:00–17:00 CET". */
  window: string;
  /** Priorities covered around the clock rather than only inside the window. */
  aroundTheClock: Priority[];
  /** EUR per quarter for availability, paid whether or not anything is reported. */
  retainer: number;
  targets: SlaTarget[];
}

export interface Incident {
  id: string;
  /** The short name it goes by. Every list shows this and only this. */
  title: string;
  /** The write-up, blank until somebody writes one. Only the incident's own page shows it. */
  description: string;
  priority: Priority;
  channel: IncidentChannel;
  /** ISO instant the incident reached us. */
  reportedAt: string;
  /** ISO instant a human first answered. Null while nobody has. */
  respondedAt: string | null;
  /** ISO instant it was fixed or worked around. Null while nobody has said it is. */
  resolvedAt: string | null;
  /** ISO instant a manager was done with the record. Not `resolvedAt`: that is when the thing was fixed. */
  closedAt: string | null;
  /** Hours booked against it, as typed when it was logged. */
  hours: number;
  /** Who logged it. */
  loggedByName?: string;
  /** Whether the client reported it themselves, which decides whether their own tab shows it to them. */
  loggedByClient?: boolean;
  /** The stretches nothing was ours to do. */
  pauses?: IncidentPause[];
}

/** Whether a target was met, missed, or is still running. */
export type SlaOutcome = 'met' | 'missed' | 'pending';

/** Where an incident that is still open stands against its targets at this moment. */
export interface OpenStanding {
  /** Wall-clock minutes since it came in. */
  waitingMinutes: number;
  /** Minutes left to answer; negative once past the target. Null once answered, or with no agreement. */
  responseDueInMinutes: number | null;
  /** Minutes left to resolve; negative once past the target. */
  resolutionDueInMinutes: number | null;
}

export interface IncidentView extends Incident {
  /** Wall-clock minutes to first response, null while nobody has answered. */
  responseMinutes: number | null;
  /** Wall-clock minutes to resolution, null while it is open. */
  resolutionMinutes: number | null;
  responseOutcome: SlaOutcome;
  resolutionOutcome: SlaOutcome;
  /** The targets this incident is judged against. Null once the product is off support. */
  target: SlaTarget | null;
  /** How it stands right now, while it is open. Null once resolved. */
  open: OpenStanding | null;
}

/** An incident known to be still open, so its standing can be read without a null check. */
export interface OpenIncidentView extends IncidentView {
  open: OpenStanding;
}

export const isOpen = (view: IncidentView): view is OpenIncidentView => view.open !== null;

/** Whether we are done with the record, which is what the queue splits on rather than whether it is fixed. */
export const isClosed = (incident: { closedAt: string | null }): boolean => !!incident.closedAt;

/** Fixed, and still on somebody's queue: the state that exists because the two are separate. */
export const isAwaitingClosure = (view: IncidentView): boolean => !isClosed(view) && !isOpen(view);

export interface PriorityRollup {
  priority: Priority;
  count: number;
  target: SlaTarget;
  responseMet: number;
  responseMissed: number;
  resolutionMet: number;
  resolutionMissed: number;
  /** Median response across the incidents that have been answered, null when none have. */
  medianResponseMinutes: number | null;
}

/** The counters a set of incidents adds up to, whether that set is one product's or the whole org's. */
export interface IncidentTotals {
  count: number;
  /** Not yet resolved, whatever their priority. */
  openCount: number;
  hours: number;
  /** Met over judged, for the compliance figures. A pending target is judged by neither. */
  responseMet: number;
  responseJudged: number;
  resolutionMet: number;
  resolutionJudged: number;
  medianResponseMinutes: number | null;
}

export interface IncidentsView extends IncidentTotals {
  incidents: IncidentView[];
  byPriority: PriorityRollup[];
  /** The same quarter a year earlier, for the "up from" line. Null when there is nothing comparable. */
  previousCount: number | null;
}

export type Quarter = 1 | 2 | 3 | 4;

export interface IncidentPeriod {
  year: number;
  quarter: Quarter;
}

/** The targets each level buys. */
const LEVEL_TARGETS: Record<SupportLevel, SlaTarget[]> = {
  standard: [
    { priority: 'P1', responseHours: 2, resolutionHours: 8, rateMultiplier: 1.5 },
    { priority: 'P2', responseHours: 2, resolutionHours: 24, rateMultiplier: 1.25 },
    { priority: 'P3', responseHours: 4, resolutionHours: 40, rateMultiplier: 1 },
    { priority: 'P4', responseHours: 8, resolutionHours: 120, rateMultiplier: 1 },
  ],
  bronze: [
    { priority: 'P1', responseHours: 1, resolutionHours: 8, rateMultiplier: 1.75 },
    { priority: 'P2', responseHours: 2, resolutionHours: 24, rateMultiplier: 1.4 },
    { priority: 'P3', responseHours: 4, resolutionHours: 40, rateMultiplier: 1 },
    { priority: 'P4', responseHours: 8, resolutionHours: 120, rateMultiplier: 1 },
  ],
  silver: [
    { priority: 'P1', responseHours: 0.5, resolutionHours: 8, rateMultiplier: 2 },
    { priority: 'P2', responseHours: 2, resolutionHours: 24, rateMultiplier: 1.5 },
    { priority: 'P3', responseHours: 4, resolutionHours: 40, rateMultiplier: 1 },
    { priority: 'P4', responseHours: 8, resolutionHours: 120, rateMultiplier: 1 },
  ],
  gold: [
    { priority: 'P1', responseHours: 0.5, resolutionHours: 8, rateMultiplier: 2 },
    { priority: 'P2', responseHours: 2, resolutionHours: 24, rateMultiplier: 1.75 },
    { priority: 'P3', responseHours: 4, resolutionHours: 40, rateMultiplier: 1 },
    { priority: 'P4', responseHours: 8, resolutionHours: 120, rateMultiplier: 1 },
  ],
};

/** What each level covers, from the agreement's own table. */
const LEVEL_SHAPE: Record<SupportLevel, Omit<SupportAgreement, 'level' | 'targets'>> = {
  standard: { window: 'Mon–Fri, 09:00–17:00 CET', aroundTheClock: [], retainer: 1500 },
  bronze: { window: 'Mon–Fri, 08:00–20:00 CET', aroundTheClock: [], retainer: 3000 },
  silver: { window: 'Daily, 08:00–20:00 CET', aroundTheClock: [], retainer: 6000 },
  gold: { window: 'Daily, 08:00–20:00 CET', aroundTheClock: ['P1', 'P2'], retainer: 9000 },
};

export const agreementFor = (level: SupportLevel): SupportAgreement => ({
  level,
  ...LEVEL_SHAPE[level],
  targets: LEVEL_TARGETS[level],
});

const MINUTES = 60 * 1000;

/** One incident off the wire. */
export const incidentFromApi = (entity: IncidentEntity): Incident => ({
  id: entity.id,
  title: entity.title,
  description: entity.description ?? '',
  priority: entity.priority.toUpperCase() as Priority,
  channel: entity.channel ?? 'email',
  reportedAt: entity.reportedAt,
  respondedAt: entity.respondedAt ?? null,
  resolvedAt: entity.resolvedAt ?? null,
  closedAt: entity.closedAt ?? null,
  hours: entity.hours ?? 0,
  loggedByName: entity.loggedByName,
  pauses: (entity.pauses ?? []).map(pause => ({
    id: pause.id,
    startedAt: pause.startedAt,
    endedAt: pause.endedAt ?? null,
    reason: pause.reason ?? null,
  })),
});

/** Hours are booked to the quarter hour, so two places is the most a sum of them can need. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

/** The agreement a level buys. Null means the product is not under support, which is a real answer. */
export const agreementForLevel = (level: SupportLevel | null | undefined): SupportAgreement | null =>
  level ? agreementFor(level) : null;

/** One incident with the product it is on, which is the shape the organization-wide list reads. */
export interface ProductIncident {
  productId: string;
  productName: string;
  incident: Incident;
}

export const productIncidentFromApi = (entity: IncidentEntity): ProductIncident => ({
  productId: entity.productId,
  productName: entity.productName,
  incident: incidentFromApi(entity),
});

/** A product as the organization-wide page reads it: enough to judge its incidents against. */
export interface SupportedProduct {
  id: string;
  name: string;
  supportLevel?: SupportLevel | null;
}

const byProduct = (rows: ProductIncident[]): Map<string, Incident[]> => {
  const grouped = new Map<string, Incident[]>();
  rows.forEach(row => grouped.set(row.productId, [...(grouped.get(row.productId) ?? []), row.incident]));
  return grouped;
};

const periodOf = (isoInstant: string): IncidentPeriod => {
  const at = new Date(isoInstant);
  return { year: at.getUTCFullYear(), quarter: (Math.floor(at.getUTCMonth() / 3) + 1) as Quarter };
};

const samePeriod = (a: IncidentPeriod, b: IncidentPeriod): boolean => a.year === b.year && a.quarter === b.quarter;

/** The quarters the log covers, most recent first, so the picker only offers quarters that exist. */
export const incidentPeriods = (incidents: Incident[]): IncidentPeriod[] => {
  const keys = new Set(incidents.map(incident => JSON.stringify(periodOf(incident.reportedAt))));
  return [...keys]
    .map(key => JSON.parse(key) as IncidentPeriod)
    .sort((a, b) => b.year - a.year || b.quarter - a.quarter);
};

/** The quarter to open on: the one in progress, since an open incident is the reason to look. */
export const defaultIncidentPeriod = (today = new Date()): IncidentPeriod => ({
  year: today.getFullYear(),
  quarter: (Math.floor(today.getMonth() / 3) + 1) as Quarter,
});

const median = (values: number[]): number | null => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : Math.round((sorted[middle - 1] + sorted[middle]) / 2);
};

/** One incident against its target. */
export const viewIncident = (
  incident: Incident,
  agreement: SupportAgreement | null,
  now = new Date(),
): IncidentView => {
  const target = agreement
    ? (agreement.targets.find(entry => entry.priority === incident.priority) ??
      ({ priority: incident.priority, responseHours: 0, resolutionHours: 0, rateMultiplier: 1 } as SlaTarget))
    : null;

  const startMs = Date.parse(incident.reportedAt);
  // The waiting, merged, with anything still running counted up to this moment: see `incidentPauses`.
  const spans = pauseSpans(incident.pauses ?? [], now.getTime());

  // Both clocks stop while we wait on somebody else, and the drawn timeline measures them the same way.
  const responseMinutes = incident.respondedAt
    ? Math.round(workingMsBetween(spans, startMs, Date.parse(incident.respondedAt)) / MINUTES)
    : null;
  const resolutionMinutes = incident.resolvedAt
    ? Math.round(workingMsBetween(spans, startMs, Date.parse(incident.resolvedAt)) / MINUTES)
    : null;

  // Nothing to judge against without an agreement, which is not the same as having met or missed one.
  const outcome = (elapsedMinutes: number | null, targetHours: number | undefined): SlaOutcome => {
    if (elapsedMinutes === null || targetHours === undefined) return 'pending';
    return elapsedMinutes <= targetHours * 60 ? 'met' : 'missed';
  };

  // Both run from when it came in; the resolution deadline is pushed out by whatever was waited on.
  const dueIn = (targetHours: number, paused = false): number => {
    const targetMs = startMs + targetHours * 60 * MINUTES;
    const deadline = paused ? deadlineAfterPauses(startMs, targetMs, spans) : targetMs;

    return Math.round((deadline - now.getTime()) / MINUTES);
  };

  return {
    ...incident,
    responseMinutes,
    resolutionMinutes,
    responseOutcome: outcome(responseMinutes, target?.responseHours),
    resolutionOutcome: outcome(resolutionMinutes, target?.resolutionHours),
    target,
    open: incident.resolvedAt
      ? null
      : {
          // Wall clock on purpose: how long the client has lived with it does not stop for a pause.
          waitingMinutes: Math.max(0, Math.round((now.getTime() - startMs) / MINUTES)),
          responseDueInMinutes: target && !incident.respondedAt ? dueIn(target.responseHours) : null,
          resolutionDueInMinutes: target ? dueIn(target.resolutionHours, true) : null,
        },
  };
};

const rollup = (views: IncidentView[], agreement: SupportAgreement): PriorityRollup[] =>
  PRIORITIES.map(priority => {
    const forPriority = views.filter(view => view.priority === priority);
    return {
      priority,
      count: forPriority.length,
      target: agreement.targets.find(entry => entry.priority === priority) as SlaTarget,
      responseMet: forPriority.filter(view => view.responseOutcome === 'met').length,
      responseMissed: forPriority.filter(view => view.responseOutcome === 'missed').length,
      resolutionMet: forPriority.filter(view => view.resolutionOutcome === 'met').length,
      resolutionMissed: forPriority.filter(view => view.resolutionOutcome === 'missed').length,
      medianResponseMinutes: median(
        forPriority.map(view => view.responseMinutes).filter((value): value is number => value !== null),
      ),
    };
  });

/** The counters over a set of already-judged incidents. */
export const totalsFrom = (views: IncidentView[]): IncidentTotals => ({
  count: views.length,
  openCount: views.filter(view => view.resolvedAt === null).length,
  hours: round2(views.reduce((total, view) => total + view.hours, 0)),
  responseMet: views.filter(view => view.responseOutcome === 'met').length,
  responseJudged: views.filter(view => view.responseOutcome !== 'pending').length,
  resolutionMet: views.filter(view => view.resolutionOutcome === 'met').length,
  resolutionJudged: views.filter(view => view.resolutionOutcome !== 'pending').length,
  medianResponseMinutes: median(
    views.map(view => view.responseMinutes).filter((value): value is number => value !== null),
  ),
});

/** One quarter: every incident reported in it, judged against the agreement, newest first. */
export const buildIncidentsView = (
  incidents: Incident[],
  /** Null on a product with no agreement: its incidents are real, they are just judged against nothing. */
  agreement: SupportAgreement | null,
  period: IncidentPeriod,
): IncidentsView => {
  const views = incidents
    .filter(incident => samePeriod(periodOf(incident.reportedAt), period))
    .map(incident => viewIncident(incident, agreement))
    .sort((a, b) => Date.parse(b.reportedAt) - Date.parse(a.reportedAt));

  const priorYear = incidents.filter(incident =>
    samePeriod(periodOf(incident.reportedAt), { year: period.year - 1, quarter: period.quarter }),
  );

  return {
    ...totalsFrom(views),
    incidents: views,
    byPriority: agreement ? rollup(views, agreement) : [],
    // Only where the year-earlier quarter is in the log: no log and no incidents count the same and mean opposite things.
    previousCount: incidents.some(incident => periodOf(incident.reportedAt).year === period.year - 1)
      ? priorYear.length
      : null,
  };
};

/** One product's quarter, for the organisation-wide page's per-product table. */
export interface ProductIncidents {
  productId: string;
  productName: string;
  agreement: SupportAgreement;
  /** This product's incidents in the quarter, each already judged against its own agreement. */
  incidents: IncidentView[];
  totals: IncidentTotals;
}

/** Every product under support, for one quarter. */
export const buildOrganizationIncidents = (
  products: SupportedProduct[],
  rows: ProductIncident[],
  period: IncidentPeriod,
): ProductIncidents[] => {
  const logs = byProduct(rows);

  return (
    products
      .filter(product => !!product.supportLevel)
      .map(product => {
        const agreement = agreementFor(product.supportLevel as SupportLevel);
        const incidents = (logs.get(product.id) ?? [])
          .filter(incident => samePeriod(periodOf(incident.reportedAt), period))
          .map(incident => viewIncident(incident, agreement))
          .sort((a, b) => Date.parse(b.reportedAt) - Date.parse(a.reportedAt));

        return {
          productId: product.id,
          productName: product.name,
          agreement,
          incidents,
          totals: totalsFrom(incidents),
        };
      })
      // Something open first, then by volume: the page is read to find what still needs answering.
      .sort(
        (a, b) =>
          b.totals.openCount - a.totals.openCount ||
          b.totals.count - a.totals.count ||
          a.productName.localeCompare(b.productName),
      )
  );
};

/** One incident as a picker row. `t` is passed in so this file stays free of i18n. */
export const incidentOption = (
  incident: { priority: string; title: string; resolvedAt?: string | null },
  t: (key: string) => string,
): { label: string; tags: { text: string; tone?: 'neutral' | 'warning' }[] } => ({
  label: `${incident.priority.toUpperCase()} - ${incident.title}`,
  // Already fixed reads in the quiet tone: it is why hours booked against it are a write-up, not a repair.
  tags: incident.resolvedAt ? [{ text: t('incidents.statusResolved'), tone: 'neutral' as const }] : [],
});

/** The resolved table reads newest-fixed first, which is the order somebody catching up reads it in. */
export const byRecentlyResolved = (a: IncidentView, b: IncidentView): number =>
  Date.parse(b.resolvedAt ?? b.reportedAt) - Date.parse(a.resolvedAt ?? a.reportedAt);

/** The queue order for open incidents: least time left first, so the top of the list is the work. */
export const byTimeLeft = (a: IncidentView, b: IncidentView): number => {
  // Deadline only: a P1 leads an older P4 because its deadline is sooner, not because it is a P1.
  if (a.open && b.open) {
    const left = a.open.resolutionDueInMinutes;
    const right = b.open.resolutionDueInMinutes;
    if (left !== null && right !== null) return left - right;
    if (left === right) return 0;
    // No agreement, no deadline: nothing to be sooner than, so those sit under the ones that have one.
    return left === null ? 1 : -1;
  }
  if (a.open) return -1;
  if (b.open) return 1;

  return byRecentlyResolved(a, b);
};

/** The queue's two halves, each in the order its own half is read in. */
export const splitIncidents = (views: IncidentView[]): { open: OpenIncidentView[]; resolved: IncidentView[] } => ({
  open: views.filter(isOpen).sort(byTimeLeft),
  resolved: views.filter(view => !isOpen(view)).sort(byRecentlyResolved),
});

/** One incident, with the product it is on carried alongside it. */
export interface OrganizationIncidentRow {
  productId: string;
  productName: string;
  incident: IncidentView;
}

/** Every incident in the organization, judged against its product's agreement where there is one. */
const viewsByProduct = (
  products: SupportedProduct[],
  rows: ProductIncident[],
  now: Date,
): OrganizationIncidentRow[] => {
  const logs = byProduct(rows);

  // Every product, agreement or not: the record outlives the agreement it was judged against.
  return products.flatMap(product => {
    const agreement = agreementForLevel(product.supportLevel);
    return (logs.get(product.id) ?? [])
      .map(incident => viewIncident(incident, agreement, now))
      .map(incident => ({ productId: product.id, productName: product.name, incident }));
  });
};

/** Everything unresolved, most pressing first. No period filter: it would hide the oldest line. */
export const buildOpenIncidents = (
  products: SupportedProduct[],
  rows: ProductIncident[],
  now = new Date(),
): OrganizationIncidentRow[] =>
  viewsByProduct(products, rows, now)
    .filter(row => !isClosed(row.incident))
    .sort((a, b) => byTimeLeft(a.incident, b.incident));

/** Everything closed across every product under support, newest first. No period: the page pages by year. */
export const buildClosedIncidents = (
  products: SupportedProduct[],
  rows: ProductIncident[],
  now = new Date(),
): OrganizationIncidentRow[] =>
  viewsByProduct(products, rows, now)
    .filter(row => isClosed(row.incident))
    .sort((a, b) => byRecentlyResolved(a.incident, b.incident));

/** Whether the thing is fixed, which is the state the queue is narrowed on. */
export type IncidentStatus = 'unresolved' | 'resolved';

// Unresolved first: it is what somebody filtering a queue of work is after.
export const INCIDENT_STATUSES: IncidentStatus[] = ['unresolved', 'resolved'];

/** What the incident lists narrow on. Every list takes the same four, so they read the same. */
export interface IncidentFilters {
  productIds: string[];
  priorities: Priority[];
  statuses: IncidentStatus[];
  /** The chosen sprints themselves, so a row can be matched on its dates without looking them up. */
  sprints: SprintSpan[];
}

export const noIncidentFilters = (): IncidentFilters => ({
  productIds: [],
  priorities: [],
  statuses: [],
  sprints: [],
});

export const hasIncidentFilters = (filters: IncidentFilters): boolean =>
  !!filters.productIds.length || !!filters.priorities.length || !!filters.statuses.length || !!filters.sprints.length;

/** Whether a row survives the bar. Status reads `resolvedAt`; the Open/Closed switch is the other question. */
export const matchesIncidentFilters = (row: OrganizationIncidentRow, filters: IncidentFilters): boolean => {
  const status: IncidentStatus = row.incident.resolvedAt ? 'resolved' : 'unresolved';

  return (
    (!filters.productIds.length || filters.productIds.includes(row.productId)) &&
    (!filters.priorities.length || filters.priorities.includes(row.incident.priority)) &&
    (!filters.statuses.length || filters.statuses.includes(status)) &&
    (!filters.sprints.length || filters.sprints.some(sprint => reportedInSprint(row.incident.reportedAt, sprint)))
  );
};

/** A year's worth of the archive, as the pager pages it. */
export interface IncidentYearPage<T> {
  year: number;
  rows: T[];
}

/** Grouped by the year each was dealt with, newest first. A year with nothing in it is not a page. */
export const incidentYearPages = <T extends { incident: IncidentView }>(rows: T[]): IncidentYearPage<T>[] => {
  const byYear = new Map<number, T[]>();

  rows.forEach(row => {
    const year = new Date(row.incident.resolvedAt ?? row.incident.reportedAt).getFullYear();
    byYear.set(year, [...(byYear.get(year) ?? []), row]);
  });

  return [...byYear.entries()].sort(([a], [b]) => b - a).map(([year, yearRows]) => ({ year, rows: yearRows }));
};

/** How hard a deadline is pressing, which is what the row is coloured by. */
export type DueTone = 'overdue' | 'soon' | 'ok';

// A fraction rather than fixed hours, so it means the same to a P1 and a P4.
const DUE_SOON_FRACTION = 0.25;

export const dueTone = (dueInMinutes: number, targetHours: number): DueTone => {
  if (dueInMinutes < 0) return 'overdue';
  return dueInMinutes <= targetHours * 60 * DUE_SOON_FRACTION ? 'soon' : 'ok';
};

/** Elapsed time as a person would say it: "40m", "3h 10m", "2d 4h". */
export const formatElapsed = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days}d ${rest}h` : `${days}d`;
};

/** A target as a person would say it: minutes below an hour, hours below a day, days above. */
export const formatTarget = (hours: number): string => {
  // The tightest promise in the agreement is half an hour, and `0.5h` is not how anybody says it.
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days}d ${rest}h` : `${days}d`;
};

/** One person's time against an incident. `hours` is derived from the stored minutes, so log and timesheet cannot disagree. */
export interface IncidentEntry {
  id: string;
  name: string;
  /** Whose entry it is. Carried so a row can be opened for correction without reassigning it. */
  userId: string;
  /** The person's chapter on the product, already labelled by the API. It follows the person. */
  chapterLabel: string;
  /** ISO instant at the start of the day it was booked on. */
  workday: string;
  hours: number;
  today: string;
  /** Null on an entry booked before the form asked for an hour, which is not an error. */
  startedAt: string | null;
  /** Whether that start fell outside the service window. Null with no start. */
  outOfHours: boolean | null;
}

export const incidentEntryFromApi = (entity: IncidentWorkEntity): IncidentEntry => ({
  id: entity.id,
  name: entity.name,
  userId: entity.userId,
  chapterLabel: entity.chapterLabel,
  workday: entity.workday,
  hours: round2(entity.minutes / 60),
  today: entity.today,
  startedAt: entity.startedAt ?? null,
  outOfHours: entity.outOfHours ?? null,
});

export interface IncidentWorkChapter {
  label: string;
  hours: number;
  entries: IncidentEntry[];
}

export interface IncidentWorkDay {
  /** ISO instant at the start of the day, for the report's own date formatting. */
  date: string;
  hours: number;
  chapters: IncidentWorkChapter[];
}

/** Grouped as the sprint report's own log renders it: day, then chapter, then person. */
export const groupEntries = (entries: IncidentEntry[]): IncidentWorkDay[] => {
  const days: IncidentWorkDay[] = [];
  [...entries]
    .sort((a, b) => a.workday.localeCompare(b.workday))
    .forEach(entry => {
      const date = entry.workday;
      const day = days.find(current => current.date === date) ?? { date, hours: 0, chapters: [] };
      if (!days.includes(day)) days.push(day);
      const chapter = day.chapters.find(current => current.label === entry.chapterLabel) ?? {
        label: entry.chapterLabel,
        hours: 0,
        entries: [],
      };
      if (!day.chapters.includes(chapter)) day.chapters.push(chapter);

      chapter.entries.push(entry);
      chapter.hours = round2(chapter.hours + entry.hours);
      day.hours = round2(day.hours + entry.hours);
    });

  return days;
};

export const hoursFromEntries = (entries: IncidentEntry[]): number =>
  round2(entries.reduce((sum, entry) => sum + entry.hours, 0));

/** An incident being entered or corrected. Fields stay as typed until they are saved. */
export interface IncidentDraft {
  id: string;
  title: string;
  /** Free text, and never required: an incident is logged in the minute it arrives. */
  description: string;
  /** Blank until somebody picks one: nothing on the log-an-incident form arrives chosen. */
  priority: Priority | '';
  /** `datetime-local` values (yyyy-mm-ddThh:mm), blank while nothing has happened yet. */
  reportedAt: string;
  respondedAt: string;
  resolvedAt: string;
}

/** Only the fields that can be wrong. A description is free text, so nothing can be wrong with it. */
export type IncidentDraftField = 'title' | 'priority' | 'reportedAt' | 'respondedAt' | 'resolvedAt';

const isBlank = (raw: string): boolean => raw.trim() === '';

/** An ISO instant as the value a `datetime-local` input takes, in the reader's own timezone. */
export const toLocalInput = (isoInstant: string | null): string => {
  if (!isoInstant) return '';
  const at = new Date(isoInstant);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

const fromLocalInput = (value: string): string | null => {
  if (isBlank(value)) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
};

let lastDraftId = 0;
const nextDraftId = (): string => {
  lastDraftId += 1;
  return `incident-draft-${lastDraftId}`;
};

/** A blank incident. */
export const emptyDraft = (today = new Date()): IncidentDraft => ({
  id: nextDraftId(),
  title: '',
  description: '',
  priority: '',
  reportedAt: toLocalInput(today.toISOString()),
  respondedAt: '',
  resolvedAt: '',
});

/** What is wrong with the draft, field by field. */
export const draftErrors = (draft: IncidentDraft): Partial<Record<IncidentDraftField, string>> => {
  const errors: Partial<Record<IncidentDraftField, string>> = {};
  if (isBlank(draft.title)) errors.title = 'title';
  if (!draft.priority) errors.priority = 'priority';

  const reported = fromLocalInput(draft.reportedAt);
  if (!reported) errors.reportedAt = 'reportedAt';

  const responded = fromLocalInput(draft.respondedAt);
  if (!isBlank(draft.respondedAt) && !responded) errors.respondedAt = 'invalid';
  else if (reported && responded && Date.parse(responded) < Date.parse(reported)) errors.respondedAt = 'beforeReported';

  const resolved = fromLocalInput(draft.resolvedAt);
  // Filed against the answer, the field a form still carries: under the fix time it would disable Save with nothing on screen.
  if (!isBlank(draft.resolvedAt) && !resolved) errors.resolvedAt = 'invalid';
  else if (resolved && !responded) errors.respondedAt = 'resolvedUnanswered';
  else if (responded && resolved && Date.parse(resolved) < Date.parse(responded))
    errors.respondedAt = 'beforeResponded';

  return errors;
};

export const isDraftValid = (draft: IncidentDraft): boolean => Object.keys(draftErrors(draft)).length === 0;

/** The draft as the API takes it. */
export const incidentInputFromDraft = (draft: IncidentDraft): IncidentInput => ({
  title: draft.title.trim(),
  // Always sent, blank included: absent would leave a write-up somebody has just deleted in place.
  description: draft.description.trim(),
  priority: draft.priority.toLowerCase() as IncidentPriorityValue,
  // No channel: the form cannot know, and Slack sets its own. Guessing would record something untrue.
  reportedAt: fromLocalInput(draft.reportedAt) as string,
  respondedAt: fromLocalInput(draft.respondedAt),
  resolvedAt: fromLocalInput(draft.resolvedAt),
});

/** What somebody typed into a local date-time box, as the instant it names. */
export const localToIso = (atLocal: string): string => fromLocalInput(atLocal) as string;

/** Saying when an incident was fixed: the moment, and nothing else, so no other field is blanked. */
export const resolutionPatch = (atLocal: string): IncidentPatch => ({ resolvedAt: fromLocalInput(atLocal) });

/** What is wrong with the moment given for when an incident was fixed. Null once it will hold. */
export const resolveError = (incident: Incident, atLocal: string): string | null => {
  const at = fromLocalInput(atLocal);
  if (!at) return isBlank(atLocal) ? 'resolvedAtRequired' : 'invalid';
  if (Date.parse(at) < Date.parse(incident.reportedAt)) return 'resolvedBeforeReported';
  if (incident.respondedAt && Date.parse(at) < Date.parse(incident.respondedAt)) return 'beforeResponded';
  return null;
};

/** A saved incident as a draft, which is what the editor on its own page opens with. */
export const draftFromIncident = (incident: Incident): IncidentDraft => ({
  id: incident.id,
  title: incident.title,
  description: incident.description,
  priority: incident.priority,
  reportedAt: toLocalInput(incident.reportedAt),
  respondedAt: toLocalInput(incident.respondedAt),
  resolvedAt: toLocalInput(incident.resolvedAt),
});
